import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill-new";

import "react-quill-new/dist/quill.snow.css";
import "./RichTextEditor.scss";

// 글자 크기를 px 단위로 세밀하게 지정할 수 있도록 화이트리스트 등록 (모듈 로드 시 1회)
const SizeStyle = Quill.import("attributors/style/size");
SizeStyle.whitelist = ["12px", "14px", "16px", "18px", "20px", "24px", "32px"];
Quill.register(SizeStyle, true);

const SIZE_OPTIONS = ["12px", "14px", false, "18px", "20px", "24px", "32px"];

// 글꼴(한글 웹폰트 포함) 화이트리스트 등록. 클래스 기반(ql-font-*)이며,
// 실제 font-family 매핑과 드롭다운 라벨은 RichTextEditor.scss / TuiViewer.scss 에서 처리한다.
const FONT_OPTIONS = ["noto-sans-kr", "nanum-gothic", "nanum-myeongjo", "gowun-dodum", "gaegu", "serif", "monospace"];
const Font = Quill.import("formats/font");
Font.whitelist = FONT_OPTIONS;
Quill.register(Font, true);

/**
 * Quill 기반 WYSIWYG 에디터 (공통 컴포넌트).
 * - 글자 크기/색상/밑줄/두께/글꼴 등 서식 지원
 * - 이미지 첨부 시 서버 업로드 후 반환된 URL을 본문에 삽입
 * - 드래그 앤 드롭 및 클립보드 붙여넣기로 이미지 업로드
 * - 저장된 본문 HTML 은 TuiViewer 로 동일하게 렌더링된다.
 *
 * @param {string}   value        본문 HTML
 * @param {Function} onChange     (html: string) => void
 * @param {Function} uploadImage  (file: File) => Promise<string> — 업로드 후 접근 가능한 URL 반환
 * @param {string}   [placeholder]
 * @param {number}   [height=420]
 */
const RichTextEditor = ({ value, onChange, uploadImage, placeholder, height = 420 }) => {
    const quillRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    // 부모가 렌더마다 새 함수를 넘겨도 modules/effect 의존성이 바뀌지 않도록 ref로 고정.
    // (의존성이 바뀌면 ReactQuill이 에디터를 파괴·재생성하며 "non-instantiated editor" 에러 발생)
    const uploadImageRef = useRef(uploadImage);
    useEffect(() => {
        uploadImageRef.current = uploadImage;
    }, [uploadImage]);

    // 던지는 getEditor() 대신 공개 필드를 직접 읽어 인스턴스 미생성 시 null 반환
    const getQuill = useCallback(() => quillRef.current?.editor ?? null, []);

    // 파일 1개를 업로드하고 현재 커서 위치에 삽입
    const insertImageFile = useCallback(
        async (file) => {
            if (!file.type.startsWith("image/")) return;
            const editor = getQuill();
            if (!editor) return;

            setUploading(true);
            try {
                const url = await uploadImageRef.current(file);
                const range = editor.getSelection(true);
                const index = range ? range.index : editor.getLength();
                editor.insertEmbed(index, "image", url, "user");
                editor.setSelection(index + 1, 0);
            } catch (e) {
                // eslint-disable-next-line no-alert
                alert("이미지 업로드에 실패했습니다. 다시 시도해주세요.");
            } finally {
                setUploading(false);
            }
        },
        [getQuill],
    );

    // 툴바 이미지 버튼: 파일 선택 → 업로드 → 삽입
    const imageHandler = useCallback(() => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/*");
        input.onchange = () => {
            const file = input.files?.[0];
            if (file) void insertImageFile(file);
        };
        input.click();
    }, [insertImageFile]);

    // modules는 한 번만 생성되어 에디터가 재생성되지 않도록 고정. imageHandler는 ref로 호출.
    const imageHandlerRef = useRef(imageHandler);
    useEffect(() => {
        imageHandlerRef.current = imageHandler;
    }, [imageHandler]);

    const modules = useMemo(
        () => ({
            toolbar: {
                container: [
                    [{ font: FONT_OPTIONS }],
                    [{ size: SIZE_OPTIONS }],
                    [{ header: [1, 2, 3, false] }],
                    ["bold", "italic", "underline", "strike"],
                    [{ color: [] }, { background: [] }],
                    [{ list: "ordered" }, { list: "bullet" }],
                    [{ align: [] }],
                    ["link", "image"],
                    ["clean"],
                ],
                handlers: { image: () => imageHandlerRef.current() },
            },
            clipboard: { matchVisual: false },
        }),
        [],
    );

    const formats = useMemo(
        () => ["font", "size", "header", "bold", "italic", "underline", "strike", "color", "background", "list", "align", "link", "image"],
        [],
    );

    // 드래그 앤 드롭 / 붙여넣기로 이미지 업로드 (마운트 시 1회 등록)
    useEffect(() => {
        const root = getQuill()?.root;
        if (!root) return;

        const handleDrop = (e) => {
            const files = Array.from(e.dataTransfer?.files ?? []).filter((f) => f.type.startsWith("image/"));
            if (files.length === 0) return;
            e.preventDefault();
            e.stopPropagation();
            files.forEach((file) => void insertImageFile(file));
        };

        const handlePaste = (e) => {
            const files = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith("image/"));
            if (files.length === 0) return;
            e.preventDefault();
            files.forEach((file) => void insertImageFile(file));
        };

        root.addEventListener("drop", handleDrop, true);
        root.addEventListener("paste", handlePaste, true);
        return () => {
            root.removeEventListener("drop", handleDrop, true);
            root.removeEventListener("paste", handlePaste, true);
        };
    }, [getQuill, insertImageFile]);

    return (
        <div className="rich-text-editor" style={{ height }}>
            <ReactQuill ref={quillRef} theme="snow" value={value} onChange={onChange} modules={modules} formats={formats} placeholder={placeholder} />
            {uploading && <div className="rich-text-editor__uploading">이미지 업로드 중…</div>}
        </div>
    );
};

export default RichTextEditor;
